import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useKycAdminEditTokenValidation } from '@/hooks/useKyc';
import { useFormsTranslation } from '@/hooks/useTranslation';
import {
  adaptApiKycFormDataToLocal,
  LocalKycFormData,
} from '@/lib/kycFormAdapter';
import { KycFormData as ApiKycFormData } from '@/types';
import { AlertCircle, Loader2, ShieldX } from 'lucide-react';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

interface KycAdminEditTokenValidationGuardProps {
  children: (initialData: LocalKycFormData) => React.ReactNode;
}

/**
 * KYC Admin Edit Token Validation Guard
 * Validates admin edit tokens and provides pre-filled form data to children.
 * Uses the admin edit specific validation endpoint that returns existing form data.
 *
 * @example
 * ```tsx
 * <KycAdminEditTokenValidationGuard>
 *   {(initialData) => <KycFormTabs initialData={initialData} isAdminEdit />}
 * </KycAdminEditTokenValidationGuard>
 * ```
 */
export function KycAdminEditTokenValidationGuard({
  children,
}: KycAdminEditTokenValidationGuardProps) {
  const { token } = useParams<{ token: string }>();
  const tForms = useFormsTranslation();

  const {
    data: tokenValidation,
    isLoading,
    error,
    refetch,
  } = useKycAdminEditTokenValidation(token || '');

  // Transform API KYC data to local component format
  const initialFormData = useMemo(() => {
    if (!tokenValidation?.result?.kyc) {
      return {};
    }
    return adaptApiKycFormDataToLocal(
      tokenValidation.result.kyc as ApiKycFormData
    );
  }, [tokenValidation?.result?.kyc]);

  // Loading state
  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center p-4'>
        <Card className='w-full max-w-md'>
          <CardContent className='flex flex-col items-center justify-center py-8'>
            <Loader2 className='h-8 w-8 animate-spin text-primary mb-4' />
            <p className='text-sm text-muted-foreground text-center'>
              {tForms.t('kyc.adminEdit.validation.validatingToken')}
            </p>
            <p className='text-xs text-muted-foreground text-center mt-2'>
              {tForms.t('kyc.adminEdit.validation.loadingFormData')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state or invalid token
  if (error || !tokenValidation?.success || !tokenValidation?.result?.valid) {
    const errorMessage =
      error?.message ||
      tokenValidation?.message ||
      tForms.t('kyc.adminEdit.validation.tokenInvalidOrExpired');

    return (
      <div className='min-h-screen flex items-center justify-center p-4'>
        <Card className='w-full max-w-md'>
          <CardHeader className='text-center'>
            <div className='flex justify-center mb-4'>
              <ShieldX className='h-12 w-12 text-destructive' />
            </div>
            <CardTitle className='text-xl'>
              {tForms.t('kyc.adminEdit.access.denied')}
            </CardTitle>
            <CardDescription>
              {tForms.t('kyc.adminEdit.access.tokenInvalid')}
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>

            {tokenValidation?.result?.token_data?.expiration_date && (
              <div className='p-3 bg-muted rounded-lg'>
                <p className='text-xs text-muted-foreground'>
                  <strong>{tForms.t('kyc.adminEdit.tokenExpiry')}:</strong>{' '}
                  {new Date(
                    tokenValidation.result.token_data.expiration_date
                  ).toLocaleString()}
                </p>
              </div>
            )}

            <div className='flex flex-col gap-2'>
              <Button
                variant='outline'
                onClick={() => refetch()}
                className='w-full'
              >
                {tForms.t('kyc.adminEdit.actions.tryAgain')}
              </Button>
              <Button
                variant='ghost'
                onClick={() => window.close()}
                className='w-full'
              >
                {tForms.t('kyc.adminEdit.actions.closeWindow')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid token - render children with initial form data
  return <>{children(initialFormData)}</>;
}

export default KycAdminEditTokenValidationGuard;
